import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { Bug } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { api, ApiError } from '../../api/client'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

/**
 * "Оставить баг" — bottom-of-profile feedback section. Opens a modal with a
 * textarea + auto-attached context (route, UA, app build). Shows the user's
 * recent reports below the trigger so they know we received them.
 *
 * Server cap is 2000 chars; we enforce the same on the client to short-circuit
 * the round-trip on over-long pastes.
 */
export function BugReportSection() {
  const { t } = useTranslation()
  const location = useLocation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  const mineQuery = useQuery({
    queryKey: ['feedback', 'bug', 'mine'],
    queryFn: () => api.listMyBugReports(),
  })

  const submitMutation = useMutation({
    mutationFn: (body: { text: string }) =>
      api.submitBugReport({
        text: body.text,
        context: {
          route: location.pathname,
          userAgent: navigator.userAgent,
          screen: { width: window.innerWidth, height: window.innerHeight },
          // Build hash is exposed by Vite if injected at build time; ignore
          // when not present so dev mode doesn't error.
          buildHash: (import.meta.env.VITE_BUILD_HASH as string | undefined) ?? null,
        },
      }),
    onSuccess: () => {
      toast.success(t('bug.submit_success'))
      setOpen(false)
      setText('')
      qc.invalidateQueries({ queryKey: ['feedback', 'bug', 'mine'] })
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.body.error.message : (err as Error).message
      toast.error(msg || t('bug.submit_failed'))
    },
  })

  const trimmed = text.trim()
  const canSubmit = trimmed.length >= 5 && trimmed.length <= 2000 && !submitMutation.isPending

  const hasReports = mineQuery.data && mineQuery.data.length > 0
  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 text-xs text-sage-500 underline-offset-2 hover:text-sage-700 hover:underline"
      >
        <Bug className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} aria-hidden />
        {t('bug.cta')}
      </button>

      {hasReports && (
        <details className="text-xs text-sage-500">
          <summary className="cursor-pointer underline-offset-2 hover:text-sage-700 hover:underline">
            {t('bug.my_reports')}
          </summary>
          <div className="mt-2 flex flex-col gap-2">
            {mineQuery.data!.map((r) => (
              <div
                key={r.id}
                className="rounded-xl bg-white/60 p-3 ring-1 ring-sage-200/60 text-sm text-sage-700"
              >
                <div className="flex items-center justify-between text-xs text-sage-500">
                  <span>{new Date(r.createdAt).toLocaleString()}</span>
                  <StatusPill status={r.status} />
                </div>
                <p className="mt-1 line-clamp-3 leading-snug text-sage-800">{r.text}</p>
              </div>
            ))}
          </div>
        </details>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={t('bug.modal_title')}>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-sage-700 leading-relaxed">{t('bug.modal_hint')}</p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            rows={6}
            placeholder={t('bug.placeholder')}
            className="w-full resize-none rounded-2xl border border-sage-200 bg-white/80 p-3 text-sm text-sage-800 placeholder-sage-400 focus:border-sage-400 focus:outline-none"
          />
          <p className="text-right text-xs text-sage-500">{trimmed.length} / 2000</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="md" onClick={() => setOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={submitMutation.isPending}
              disabled={!canSubmit}
              onClick={() => submitMutation.mutate({ text: trimmed })}
            >
              {t('bug.submit')}
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  )
}

function StatusPill({ status }: { status: 'open' | 'resolved' | 'dismissed' }) {
  const { t } = useTranslation()
  const tone =
    status === 'resolved'
      ? 'bg-sage-100 text-sage-700'
      : status === 'dismissed'
        ? 'bg-sage-50 text-sage-500'
        : 'bg-[#fbeadd] text-copper'
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>
      {t(`bug.status_${status}`)}
    </span>
  )
}
